import { useLayoutEffect, useState, type ReactNode } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import Loading from '@/features/shared/components/Loading'
import { queryRetryDelay, shouldRetryQuery } from '@/features/shared/api/retryPolicy'
import { advanceAuthEpoch, resetTokenBridge } from '../tokenBridge'
import { resetAuthenticatedStores } from '../resetAuthenticatedStores'

/** A separate cache and component tree for every authenticated session/organization. */
export function SessionAppState({
  identity,
  children,
  loadingFallback = <Loading />,
}: {
  identity: string
  children: ReactNode
  loadingFallback?: ReactNode
}) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60 * 5,
            retry: shouldRetryQuery,
            retryDelay: queryRetryDelay,
            refetchOnWindowFocus: false,
          },
        },
      })
  )
  const [ready, setReady] = useState(false)
  useLayoutEffect(() => {
    advanceAuthEpoch()
    resetAuthenticatedStores()
    setReady(true)
    return () => {
      advanceAuthEpoch()
      resetTokenBridge()
      // Retired mutations can still settle after their components unmount.
      // Remove feature callbacks before clearing the cache so rollbacks cannot
      // restore the previous user's snapshots into the new session's stores.
      for (const mutation of queryClient.getMutationCache().getAll()) {
        mutation.setOptions({
          ...mutation.options,
          onSuccess: undefined,
          onError: undefined,
          onSettled: undefined,
        })
      }
      void queryClient.cancelQueries()
      queryClient.clear()
    }
  }, [identity, queryClient])
  return ready ? (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  ) : (
    loadingFallback
  )
}

export default function AuthenticatedAppState({
  children,
  loadingFallback = <Loading />,
}: {
  children: ReactNode
  loadingFallback?: ReactNode
}) {
  const { isLoaded, userId, sessionId, orgId, sessionClaims } = useAuth()
  if (!isLoaded) return loadingFallback
  const tenantId = (sessionClaims as { tenant_id?: unknown } | null)?.tenant_id
  const identity = JSON.stringify([
    userId ?? null,
    orgId ?? null,
    sessionId ?? null,
    typeof tenantId === 'string' ? tenantId : null,
  ])
  return (
    <SessionAppState key={identity} identity={identity} loadingFallback={loadingFallback}>
      {children}
    </SessionAppState>
  )
}
